import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ListingCard } from "@/components/ListingCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search as SearchIcon, Zap, ShieldCheck, Ruler, Bell } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1680789076643-180e62aa5575?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwzfHxvZmYlMjByb2FkJTIwdHJ1Y2slMjB3aGVlbHN8ZW58MHx8fHwxNzg3ODgwMzMxfDA&ixlib=rb-4.1.0&q=85";
const IMG_WHEELS = "https://images.unsplash.com/photo-1585824856765-0cab8940a879?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxvZmYlMjByb2FkJTIwdHJ1Y2slMjB3aGVlbHN8ZW58MHx8fHwxNzg3ODgwMzMxfDA&ixlib=rb-4.1.0&q=85";
const IMG_TIRES = "https://images.unsplash.com/photo-1617535909749-2ca20a0a704d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwzfHxtdWQlMjB0ZXJyYWluJTIwdGlyZXN8ZW58MHx8fHwxNzg3ODgwMzMxfDA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const navigate = useNavigate();
  const [constants, setConstants] = useState(null);
  const [listings, setListings] = useState([]);
  const [q, setQ] = useState("");
  const [bolt, setBolt] = useState("");

  useEffect(() => {
    api.get("/constants").then((r) => setConstants(r.data));
    api.get("/listings", { params: { sort: "boosted" } }).then((r) => setListings(r.data.slice(0, 8)));
  }, []);

  const runSearch = () => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (bolt) p.set("bolt_pattern", bolt);
    navigate(`/search?${p.toString()}`);
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative border-b border-border overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/70" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
        </div>
        <div className="relative max-w-[1500px] mx-auto px-4 sm:px-6 py-20 md:py-28">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-1.5 text-blaze text-xs font-bold uppercase tracking-widest mb-4">
              <Zap className="w-3.5 h-3.5" /> Truck Fitment Marketplace
            </span>
            <h1 className="font-heading font-900 text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight uppercase">
              Find wheels & tires that <span className="text-blaze">actually fit</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
              Search by exact bolt pattern, diameter, width and offset. No more guessing whether a setup clears your build.
            </p>

            {/* Search bar */}
            <div className="mt-8 bg-[#141414]/90 backdrop-blur border border-border rounded-sm p-3 flex flex-col sm:flex-row gap-3 shadow-[0_8px_32px_rgba(0,0,0,0.5)]">
              <div className="flex-1 flex items-center gap-2 bg-[#0A0A0A] border border-border rounded-sm px-3">
                <SearchIcon className="w-4 h-4 text-muted-foreground" />
                <Input
                  data-testid="hero-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && runSearch()}
                  placeholder="Search brand, model, keyword…"
                  className="border-0 bg-transparent focus-visible:ring-0 h-11 px-0"
                />
              </div>
              <Select value={bolt} onValueChange={setBolt}>
                <SelectTrigger data-testid="hero-bolt-select" className="sm:w-44 bg-[#0A0A0A] border-border rounded-sm h-11 spec-block">
                  <SelectValue placeholder="Bolt pattern" />
                </SelectTrigger>
                <SelectContent className="bg-[#141414] border-border">
                  {constants?.bolt_patterns.map((b) => <SelectItem key={b} value={b} className="spec-block">{b}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button data-testid="hero-search-btn" onClick={runSearch} className="bg-blaze hover:bg-blaze-hover text-white rounded-sm font-bold h-11 px-6 gap-2 hover:-translate-y-0.5 transition-transform">
                <SearchIcon className="w-4 h-4" /> Search
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="border-b border-border bg-[#0A0A0A]">
        <div className="max-w-[1500px] mx-auto px-4 sm:px-6 grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-border">
          {[
            { icon: Ruler, t: "Structured Specs", d: "Every listing carries exact fitment data — no vague descriptions." },
            { icon: ShieldCheck, t: "Photo Verified", d: "Real photos required. Report anything that looks off." },
            { icon: Bell, t: "Rare-Spec Alerts", d: "Save a search and get matched when new inventory lands." },
          ].map((f, i) => (
            <div key={i} className="flex items-start gap-3 py-6 sm:px-8 first:sm:pl-0">
              <f.icon className="w-6 h-6 text-blaze shrink-0 mt-0.5" />
              <div>
                <div className="font-heading font-800 text-sm uppercase tracking-tight">{f.t}</div>
                <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{f.d}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-[1500px] mx-auto px-4 sm:px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { label: "Wheels", cat: "wheels", img: IMG_WHEELS },
            { label: "Tires", cat: "tires", img: IMG_TIRES },
            { label: "Full Sets", cat: "set", img: HERO },
          ].map((c) => (
            <button
              key={c.cat}
              data-testid={`category-${c.cat}`}
              onClick={() => navigate(`/search?category=${c.cat}`)}
              className="group relative h-40 rounded-sm overflow-hidden border border-border text-left"
            >
              <img src={c.img} alt={c.label} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-black/50 group-hover:bg-black/30 transition-colors" />
              <div className="absolute bottom-0 left-0 p-4">
                <div className="font-heading font-900 text-2xl uppercase tracking-tight">{c.label}</div>
                <div className="text-xs text-blaze font-bold uppercase tracking-widest">Browse →</div>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Latest listings */}
      <section className="max-w-[1500px] mx-auto px-4 sm:px-6 pb-16">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading font-800 text-lg uppercase tracking-tight">Fresh Inventory</h2>
          <Button variant="ghost" onClick={() => navigate("/search")} className="text-blaze hover:bg-transparent hover:text-blaze-hover text-sm font-bold">View all →</Button>
        </div>
        {listings.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-border rounded-sm text-muted-foreground">
            No listings yet. <button onClick={() => navigate("/sell")} className="text-blaze hover:underline">Be the first to sell →</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {listings.map((l, i) => <ListingCard key={l.id} listing={l} index={i} />)}
          </div>
        )}
      </section>
    </div>
  );
}
